import { Goal, ID } from '@griply/types';

import { getArchivedGoalIds, getCompletedGoalIds } from '../derived-goal-state';

export type GoalTreeNode = {
  goal: Goal;
  children: GoalTreeNode[];
};

export type BuildGoalTreeOptions = {
  hideArchived?: boolean;
  hideCompleted?: boolean;
  // Unfiltered set: inherited state resolves against it so a sub-goal keeps it when a filter drops its parent.
  allGoals?: Goal[];
};

export const buildGoalTree = (
  goals: Goal[],
  { hideArchived, hideCompleted, allGoals }: BuildGoalTreeOptions = {},
): GoalTreeNode[] => {
  const archivedIds = getArchivedGoalIds(allGoals ?? goals);
  const completedIds = getCompletedGoalIds(allGoals ?? goals);
  const goalMap = new Map<ID, Goal>();
  const childrenMap = new Map<ID, Goal[]>();

  for (const goal of goals) {
    goalMap.set(goal.id, goal);
  }

  for (const goal of goals) {
    if (hideArchived && archivedIds.has(goal.id)) {
      continue;
    }
    if (hideCompleted && completedIds.has(goal.id)) {
      continue;
    }

    const directParentId = goal.parentIds?.[goal.parentIds.length - 1];
    if (directParentId && goalMap.has(directParentId)) {
      if (!childrenMap.has(directParentId)) {
        childrenMap.set(directParentId, []);
      }
      childrenMap.get(directParentId)!.push(goal);
    }
  }

  const buildNode = (goal: Goal): GoalTreeNode => {
    const children = (childrenMap.get(goal.id) || [])
      .filter((child) => {
        if (hideArchived && archivedIds.has(child.id)) {
          return false;
        }
        return !hideCompleted || !completedIds.has(child.id);
      })
      .map(buildNode);

    return { goal, children };
  };

  const rootGoals = goals.filter((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return false;
    }
    if (hideCompleted && completedIds.has(goal.id)) {
      return false;
    }

    if (!goal.parentIds?.length) {
      return true;
    }

    return !goal.parentIds.some((parentId) => goalMap.has(parentId));
  });

  return rootGoals.map(buildNode);
};
